<script setup lang="ts">
import { computed, ref } from 'vue';
import type { AuraVisualizationPlan } from '@/types';
import { formatAuraDateLabel, formatAuraMetric } from '@/utils/auraPresentation';
import AuraChartBlock from './AuraChartBlock.vue';

interface Props {
    plan: AuraVisualizationPlan;
}

const props = defineProps<Props>();
const selectedLabel = ref<string | null>(null);

const primaryChart = computed(() => props.plan.charts[0]);
const selectedIndex = computed(() => {
    if (!selectedLabel.value || !primaryChart.value?.labels) return -1;
    return primaryChart.value.labels.indexOf(selectedLabel.value);
});

const selectedDetails = computed(() => {
    const index = selectedIndex.value;
    const chart = primaryChart.value;
    if (!chart || index < 0) return null;

    const series = (chart.series || [])
        .map((item) => ({
            name: item.name,
            value: Number(item.data[index] || 0),
        }))
        .filter((item) => item.value > 0);

    return {
        label: selectedLabel.value,
        value: Number(chart.data[index] || 0),
        unit: chart.unit || '',
        series,
    };
});

const handleSelect = (params: unknown) => {
    if (!params || typeof params !== 'object' || !('name' in params)) return;
    const name = String((params as { name?: unknown }).name || '');
    selectedLabel.value = name || null;
};
</script>

<template>
    <section
        class="min-w-0 overflow-hidden rounded-2xl border border-[#DDE5EA] bg-gradient-to-br from-white via-white to-[#F2FBF9] shadow-[0_14px_40px_rgba(36,43,89,.08)]"
        aria-label="Painel visual coordenado"
    >
        <header
            class="flex flex-wrap items-start justify-between gap-3 border-b border-[#E8EEF0] p-5"
        >
            <div>
                <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-[#168F7D]">
                    Análise coordenada
                </p>
                <h3 class="mt-1 text-lg font-bold text-[#242B59]">
                    {{ plan.title || 'Visão multidimensional' }}
                </h3>
            </div>
            <span
                class="rounded-full bg-[#48DDC3]/15 px-3 py-1 text-xs font-semibold text-[#168F7D]"
            >
                {{ plan.charts.length }} perspectivas conectadas
            </span>
        </header>

        <div class="grid min-w-0 grid-cols-1 gap-4 p-4 lg:grid-cols-2">
            <AuraChartBlock
                v-for="(chart, index) in plan.charts"
                :key="chart.id || index"
                :chart="chart"
                :selected-label="selectedLabel"
                :class="['min-w-0', index === 0 ? 'lg:col-span-2' : '']"
                @select="handleSelect"
            />
        </div>

        <div
            v-if="selectedDetails"
            class="mx-4 mb-4 rounded-xl border border-[#BDEFE5] bg-[#F3FFFC] p-4"
        >
            <p class="text-[10px] font-bold uppercase tracking-[0.16em] text-[#168F7D]">
                Foco selecionado
            </p>
            <div class="mt-1 flex flex-wrap items-end justify-between gap-3">
                <p class="text-base font-semibold text-[#242B59]">
                    {{ formatAuraDateLabel(selectedDetails.label) }}
                </p>
                <p class="text-xl font-bold text-[#168F7D]">
                    {{ formatAuraMetric(selectedDetails.value, selectedDetails.unit) }}
                </p>
            </div>
            <div v-if="selectedDetails.series.length" class="mt-3 flex flex-wrap gap-2">
                <span
                    v-for="item in selectedDetails.series"
                    :key="item.name"
                    class="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-[#626468]"
                >
                    {{ item.name }}: {{ formatAuraMetric(item.value, selectedDetails.unit) }}
                </span>
            </div>
        </div>
    </section>
</template>

<style scoped>
@media (orientation: landscape) and (max-height: 500px) {
    section > header {
        gap: 0.5rem;
        padding: 0.625rem;
    }

    section > header h3 {
        font-size: 0.875rem;
    }

    section > header span {
        padding: 0.25rem 0.5rem;
        font-size: 10px;
    }

    section > div:nth-child(2) {
        gap: 0.5rem;
        padding: 0.5rem;
    }
}
</style>
